import { useTranslation } from "react-i18next";
import { DocumentText1 } from "iconsax-react";
import { Badge } from "@/shared/ui";
import { formatDateForDisplay, formatMoneyKzt } from "@/shared/utils";
import { getActPipelineBadge } from "../../../utils";
import type { GphCompletionAct } from "../../../types";

interface Props {
  act: GphCompletionAct;
  locale: "ru" | "kk" | "en";
  onClick?: () => void;
}

export default function SmallActCard({ act, locale, onClick }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const badge = getActPipelineBadge(act.status, act.is_payment_approved, act.is_paid, t);

  return (
    <div
      className="p-4 radius-lg border surface-component-stroke flex flex-col gap-3 cursor-pointer hover:surface-tertiary-fill"
      onClick={onClick}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 aspect-square radius-xs text-white radial-glow background-neutral-fill flex items-center justify-center shrink-0">
            <DocumentText1 size={16} color="currentColor" />
          </div>
          <div>
            <p className="text-body-bold-sm content-base-primary">
              {t("calculations.actDetail.actNumber", { number: act.act_number })}
            </p>
            <p className="text-body-regular-xs content-action-neutral">
              {formatDateForDisplay(act.period_start_date)} — {formatDateForDisplay(act.period_end_date)}
            </p>
          </div>
        </div>
        <Badge variant="soft" color={badge.color} text={badge.text} icon={badge.icon} />
      </div>

      {act.lines.length > 0 && (
        <div className="flex flex-col gap-1">
          {act.lines.slice(0, 3).map((line, index) => (
            <div key={line.id} className="flex items-center justify-between gap-2">
              <p className="text-body-regular-xs content-base-secondary truncate">
                {index + 1}. {line.description}
                {line.project?.name || line.project_name
                  ? ` ${t("calculations.actDetail.byProject", { project_name: line.project?.name || line.project_name })}`
                  : ""}
              </p>
              <p className="text-body-bold-xs content-base-primary shrink-0">
                {formatMoneyKzt(line.line_amount ?? 0, locale)}
              </p>
            </div>
          ))}
          {act.lines.length > 3 && (
            <p className="text-body-regular-xs content-action-neutral">
              {t("calculations.actDetail.moreLines", { count: act.lines.length - 3 })}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t surface-base-stroke">
        <p className="text-body-regular-xs content-action-neutral">{t("calculations.actDetail.total")}</p>
        <p className="text-body-bold-sm content-base-primary">{formatMoneyKzt(act.amount, locale)}</p>
      </div>
    </div>
  );
}

